import { Button, Space, Table, TableColumnsType, Tag } from 'antd';
import {
	ApplicationSummary,
	UserRole,
	Pagination,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import { ReactElement } from 'react';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import InCompleteCheck from '../../../assets/images/Incomplete-check.svg';
import CompleteCheck from '../../../assets/images/complete-check.svg';
import NotApprovedCross from '../../../assets/images/not-approved.svg';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import { useSnapshot } from 'valtio';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ApplicationStatus, ReviewAction } from '../../../shared/Constants';
import {
	isProposerSkippedToEnd,
	tablePaginationConfiguration,
} from '../../../shared/Utils';

interface ApplicationTableDataType {
	key: string;
	fullName: string;
	idCard: string;
	gender: string | undefined;
	status?: ReactElement;
	center: string | undefined;
	zone: string;
	proposerName: string | undefined;
	proposerSRCMID: string;
	application?: ReactElement;
	preceptor?: ReactElement;
	candidate?: ReactElement;
	family?: ReactElement;
	otherReference?: ReactElement;
	originalApplication: ApplicationSummary;
	preceptorRecommendation?: ReactElement;
	proposerRecommendation?: ReactElement;
	ZCRecommendation?: ReactElement;
	careTeamRecommendation?: ReactElement;
	applicationStatus?: string;
}

interface ApplicationSummaryCardProps {
	applications: ApplicationSummary[] | undefined;
	onEvaluateClick: (application: ApplicationSummary) => void;
	onVerifyResultsClick: (application: ApplicationSummary) => void;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
	pagination: Pagination;
}

export const ApplicationSummaryTableView = (
	props: ApplicationSummaryCardProps
) => {
	const {
		applications,
		onEvaluateClick,
		onVerifyResultsClick,
		onPageChange,
		pageConfig,
		pagination,
	} = props;
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { role } = useRedirectToLogin();
	const { t } = useTranslation();
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;

	const isZC = role === UserRole.ZC;
	const isCareTeam = role === UserRole.CARE_TEAM;

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleVerifyResultsClick = (application: ApplicationTableDataType) => {
		onVerifyResultsClick(application.originalApplication);
	};

	const handleEvaluateClick = (application: ApplicationTableDataType) => {
		onEvaluateClick(application.originalApplication);
	};

	const showVerifyResults =
		(role === UserRole.ZC &&
			batchInfo?.snpResultDateForZC &&
			batchInfo.snpResultDateForZC !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForZC)) ||
		(role === UserRole.APPROVER &&
			batchInfo?.snpResultDateForApprover &&
			batchInfo.snpResultDateForApprover !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForApprover));

	const applicationTableData = applications?.map((application) => {
		let statusValue = '';
		let proposerNameValue = '';
		let proposerSRCMIDValue = '';
		const applicationStatus = application?.applicationStatus;
		statusValue = getTranslatedValue(application.applicationStatus) || '';
		const isWaitingForProposer =
			applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED;
		const isProposerFinalRecommendation =
			applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED;
		proposerNameValue = application.proposerName || '';
		proposerSRCMIDValue = application.proposerSrcmId || '';

		let statusStyle = '';

		switch (applicationStatus) {
			case ApplicationStatus.APPROVED:
				statusStyle =
					'bg-[#E2F7F0] border border-solid border-green text-green';
				break;

			case ApplicationStatus.REJECTED:
				statusStyle =
					'bg-[#FEE2E2] border border-solid border-[#F04438] text-[#F04438]';
				break;

			default:
				statusStyle =
					'bg-[#FEF4E7] border border-solid border-[#F78F08] text-[#F78F08]';
				break;
		}

		const tagClassName = `${statusStyle} text-md rounded-full p-2`;

		const tableData: ApplicationTableDataType = {
			key: application.applicant.srcmid,
			fullName: `${application.applicant.firstName ?? ''} ${
				application.applicant.lastName ?? ''
			}`,
			idCard: application.applicant.srcmid,
			gender: application.applicant.gender ?? '',
			center: application.center.name ?? '',
			zone: application.center.zone,
			proposerName: proposerNameValue,
			proposerSRCMID: proposerSRCMIDValue,
			status: <Tag className={tagClassName}>{statusValue}</Tag>,
			...(isProposerFinalRecommendation
				? {}
				: {
						application: application.applicantAnswersReviewed ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						preceptor: application.preceptorInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						candidate: application.applicantInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						family: application.familyMemberInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						otherReference: application.otherReferenceProvided ? (
							<img
								src={
									application.otherReferenceInterviewDone
										? CompleteCheck
										: InCompleteCheck
								}
								alt="Check"
								className="w-5 h-5"
							/>
						) : (
							<>NA</>
						),
				  }),
			...(isWaitingForProposer
				? {}
				: {
						preceptorRecommendation: application.preceptorRecommendation
							?.review ? (
							<img
								src={
									application.preceptorRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						proposerRecommendation: application.proposerRecommendation
							?.review ? (
							<img
								src={
									application.proposerRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						ZCRecommendation: application.zcRecommendation?.review ? (
							<img
								src={
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE ||
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE_IDK_CANDIDATE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						careTeamRecommendation: !application.shouldCareTeamReview ? (
							<>NA</>
						) : application.careTeamRecommendation?.review ? (
							<img
								src={
									application.careTeamRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
				  }),
			originalApplication: application,
			applicationStatus,
		};

		return tableData;
	});

	const isWaitingForProposer = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED
	);
	const isProposerFinalRecommendation = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED
	);
	const isApproved = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.APPROVED
	);
	const isNotGoingForward = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.REJECTED
	);

	const showAllColumns = isApproved || isNotGoingForward;
	const hideInterviewColumns = isZC || isCareTeam;

	const baseColumns: TableColumnsType<ApplicationTableDataType> = [
		{
			title: t('fullName'),
			dataIndex: 'fullName',
			key: 'fullName',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
			render: (text: string, record: ApplicationTableDataType) => {
				if (showVerifyResults) {
					return (
						<span
							className="text-blue-500 cursor-pointer hover:underline max-w-[400px] whitespace-nowrap md:whitespace-normal break-words"
							onClick={() => handleVerifyResultsClick(record)}
						>
							{text}
						</span>
					);
				} else {
					return (
						<span
							className="text-blue-500 cursor-pointer hover:underline"
							onClick={() => handleEvaluateClick(record)}
						>
							{text}
						</span>
					);
				}
			},
		},
		{
			title: t('idCard'),
			dataIndex: 'idCard',
			key: 'idCard',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (__, record, index) => (
				<span key={index}>
					<span className="ml-2">
						{record?.gender
							? record.gender.charAt(0).toUpperCase() +
							  record.gender.slice(1).toLowerCase()
							: ''}
					</span>
				</span>
			),
		},
		{
			title: t('center'),
			dataIndex: 'center',
			key: 'center',
			className: 'whitespace-nowrap',
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('proposerName'),
			dataIndex: 'proposerName',
			key: 'proposerName',
			width: 150,
			className: 'whitespace-nowrap',
		},
		{
			title: t('proposerSRCMID'),
			dataIndex: 'proposerSRCMID',
			key: 'proposerSRCMID',
		},
	];

	if (!hideInterviewColumns) {
		if (isWaitingForProposer || showAllColumns) {
			baseColumns.push(
				{
					title: t('application'),
					dataIndex: 'application',
					key: 'application',
					align: 'center',
				},
				{
					title: t('candidate'),
					dataIndex: 'candidate',
					key: 'candidate',
					align: 'center',
				},
				{
					title: t('preceptor'),
					dataIndex: 'preceptor',
					key: 'preceptor',
					align: 'center',
				},
				{
					title: t('family'),
					dataIndex: 'family',
					key: 'family',
					align: 'center',
				},
				{
					title: t('otherReference'),
					dataIndex: 'otherReference',
					key: 'otherReference',
					align: 'center',
				}
			);
		}

		if (isProposerFinalRecommendation || showAllColumns) {
			baseColumns.push(
				{
					title: t('preceptorRecommendation'),
					dataIndex: 'preceptorRecommendation',
					key: 'preceptorRecommendation',
					align: 'center',
				},
				{
					title: t('proposerRecommendation'),
					dataIndex: 'proposerRecommendation',
					key: 'proposerRecommendation',
					align: 'center',
				},
				{
					title: t('ZCRecommendation'),
					dataIndex: 'ZCRecommendation',
					key: 'ZCRecommendation',
					align: 'center',
				},
				{
					title: t('careTeamRecommendation'),
					dataIndex: 'careTeamRecommendation',
					key: 'careTeamRecommendation',
					align: 'center',
				}
			);
		}
	}

	baseColumns.push({
		title: t('action'),
		key: 'operation',
		fixed: 'right',
		width: 150,
		render: (__, record) => (
			<Space size="middle" className="whitespace-nowrap">
				<Button
					type="primary"
					onClick={() => handleEvaluateClick(record)}
					className="w-fit"
				>
					{showAllColumns ? t('view') : t('evaluate')}
				</Button>
			</Space>
		),
	});

	const columns = [...baseColumns];

	columns.splice(2, 0, {
		title: t('status'),
		dataIndex: 'status',
		key: 'status',
	});

	return (
		<>
			<Table
				columns={columns}
				dataSource={applicationTableData}
				pagination={tablePaginationConfig}
				scroll={{ x: 'max-content' }}
				rowClassName={() => 'border-b border-gray-300 bg-white'}
				className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
			/>
		</>
	);
};
